import { ActionConfirmation } from "../ui/ActionConfirmation.js";
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError, confirmEmail } from "../api.js";

const MIN_PASSWORD_LENGTH = 10;

/**
 * Confirming an address, from the link in the email.
 *
 * Deliberately a button rather than something that happens on load. Mail
 * clients and security scanners follow links in messages before anybody reads
 * them — Outlook's link checking does it as a matter of course — and a
 * single-use token spent by a scanner is a token the person never gets to use.
 * A scanner will fetch this page; it will not press the button.
 *
 * Finishing a registration asks for the password. Anybody can register
 * any address, so only whoever holds this link can choose it. The first press
 * goes without one: a change of address needs none, and the server answers a
 * registration with `password_required`, spending nothing, which is when the
 * password fields appear.
 */
export function ConfirmEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [outcome, setOutcome] = useState<{ linked: boolean; moved: boolean; purchaseClaimPending?: boolean } | null>(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      setOutcome(await confirmEmail(token));
    } catch (cause: unknown) {
      if (cause instanceof ApiError && cause.code === "password_required") {
        setNeedsPassword(true);
      } else {
        setError(cause instanceof Error ? cause.message : "Could not confirm that address.");
      }
      setBusy(false);
    }
  }

  async function confirmWithPassword(event: FormEvent) {
    event.preventDefault();
    if (password !== confirmation) {
      setError("Those two passwords are not the same.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setOutcome(await confirmEmail(token, password));
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Could not confirm that address.");
      setBusy(false);
    }
  }

  if (token === "") {
    return (
      <div className="signin">
        <div style={{ width: 320 }}>
          <div className="error small">That link is incomplete. Ask for a new one.</div>
          <Link className="small" to="/register">
            Register again
          </Link>
        </div>
      </div>
    );
  }

  if (outcome) {
    return (
      <div className="signin">
        <div style={{ width: 320 }}>
          <div className="brand" style={{ padding: "0 0 6px" }}>
            Wirral Music Factory
            <small>Hub</small>
          </div>
          <ActionConfirmation variant="success" title="Email confirmation completed" description={<>
            {/* The same link serves registering and changing an address, and
                the two want different sentences: one is "you are set up", the
                other is "sign in with this one from now on". */}
            {outcome.moved
              ? "Address changed. Sign in with this one from now on — your password has not changed."
              : outcome.purchaseClaimPending
                ? "Address confirmed. Sign in to finish setting up your student and continue your purchase."
              : outcome.linked
                ? "Address confirmed. Sign in and your lessons will be there."
                : "Address confirmed. Sign in to continue setting up your student."}
           </>} />
          <Link className="small" to={!outcome.moved && (!outcome.linked || outcome.purchaseClaimPending) ? "/setup" : "/"}>
            {!outcome.moved && (!outcome.linked || outcome.purchaseClaimPending) ? "Sign in to continue setup" : "Sign in"}
          </Link>
        </div>
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="signin">
        <form onSubmit={(event) => void confirmWithPassword(event)}>
          <div className="brand" style={{ padding: "0 0 6px" }}>
            Wirral Music Factory
            <small>Hub</small>
          </div>

          {error && (
            <div className="error small" role="alert">
              {error}
            </div>
          )}

          <p className="small muted" style={{ margin: 0 }}>
            Choose the password you will use to sign in to your Hub account.
          </p>
          <label className="small muted" htmlFor="confirm-email-password">
            Choose a password
          </label>
          <input
            id="confirm-email-password"
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          <label className="small muted" htmlFor="confirm-email-confirmation">
            Password again
          </label>
          <input
            id="confirm-email-confirmation"
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            required
          />
          <p className="small muted" style={{ margin: 0 }}>
            At least {MIN_PASSWORD_LENGTH} characters.
          </p>
          <button type="submit" disabled={busy}>
            {busy ? "Confirming…" : "Finish setting up"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="signin">
      <div style={{ width: 320 }}>
        <div className="brand" style={{ padding: "0 0 6px" }}>
          Wirral Music Factory
          <small>Hub</small>
        </div>

        {error && <div className="error small">{error}</div>}

        <p className="small muted">
          Confirm this address to finish setting up the account.
        </p>

        <button type="button" onClick={() => void confirm()} disabled={busy}>
          {busy ? "Confirming…" : "Confirm this address"}
        </button>
      </div>
    </div>
  );
}
